import { useEffect, useMemo } from 'react';

import {
  SERVICE_WORKER_PATH,
  isServiceWorkerSupported,
  postActiveMembership,
} from './serviceWorkerMessages';
import { useUpsertPushSubscription } from './useWebPush';
import { useAppSelector } from 'src/app/store/hooks';

export function NotificationRegistrar() {
  const profile = useAppSelector((state) => state.auth.profile);
  const currentOrganizationId = useAppSelector(
    (state) => state.auth.currentOrganizationId
  );
  const activeMembership = useMemo(() => {
    return profile?.memberships.find(
      (membership) => membership.organization.id === currentOrganizationId
    );
  }, [profile, currentOrganizationId]);
  const upsertSubscription = useUpsertPushSubscription();

  useEffect(() => {
    if (
      !activeMembership ||
      !isServiceWorkerSupported() ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      return;
    }

    let canceled = false;
    // Read through a function so TS doesn't narrow `canceled` to false across
    // the awaits below; the cleanup can flip it while they're pending.
    const isStale = () => canceled;
    (async () => {
      try {
        await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
        // On first registration the worker may still be installing, so
        // registration.active can be null. `ready` waits for it to activate.
        const registration = await navigator.serviceWorker.ready;
        if (isStale()) {
          return;
        }
        postActiveMembership(registration.active, activeMembership.id);

        // A shared browser keeps one push endpoint across logins, and the
        // backend sends to the user who owns it. Re-upsert any existing
        // subscription so it's owned by whoever is signed in now. Only when
        // permission is already granted, so this never prompts.
        if (Notification.permission !== 'granted') {
          return;
        }
        const subscription = await registration.pushManager.getSubscription();
        if (subscription && !isStale()) {
          await upsertSubscription(subscription);
        }
      } catch (e) {
        console.error('Unable to register push notifications', e);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [activeMembership, upsertSubscription]);

  return null;
}
